import React, { useRef, useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, IconButton, Typography, useMediaQuery, useTheme } from '@mui/material';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { createWidget } from '@typeform/embed';
import '@typeform/embed/build/css/widget.css';
import { useResearch } from '@/contexts/ResearchContext';
import { useSurveyContext } from '@/contexts/SurveyContext';
import { useFeedbackMetadata } from '@/hooks/useFeedbackMetadata';
import { buildFeedbackFields } from '@/utils/feedbackMetadata';
import { releaseVelocity, sheetDragOffset, shouldDismissSheet } from '@/utils/sheetGesture';
import { BORDER_RADIUS } from '@/theme/tokens';

const TYPEFORM_FORM_ID = 'bWNbgCWe';
const SURVEY_VERSION = 'v2.0';

export const CHIP_COLOR = '#617080';

const PANEL_WIDTH = 380;
const PANEL_HEIGHT = 520;
const EDGE_MARGIN = 12;
const SIDEBAR_WIDTH = 35;
// Gap above the mobile sheet, matching the mobile app's HEADER_OFFSET
const MOBILE_SHEET_TOP_OFFSET = 60;
const SHEET_ANIMATION_MS = 280;

const FeedbackSurveyChip: React.FC = () => {
  const { t } = useTranslation('ballot');
  const { sessionId, markSurveyCompleted, panelOpen, closeSurveyPanel } = useSurveyContext();
  const { participantId, campaignCode } = useResearch();
  const metadata = useFeedbackMetadata();
  // Read at mount through a ref: the panel floats over the app, so the route can
  // change while it's open, and remounting would wipe the voter's answers.
  const metadataRef = useRef(metadata);
  metadataRef.current = metadata;
  const metadataReady = metadata !== null;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const widgetContainerRef = useRef<HTMLDivElement>(null);
  const widgetMountedRef = useRef(false);
  const widgetInstanceRef = useRef<{ unmount: () => void } | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  // Drag state
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [hasBeenDragged, setHasBeenDragged] = useState(false);
  const dragRef = useRef<{
    startX: number;
    startY: number;
    startPosX: number;
    startPosY: number;
    dragging: boolean;
    moved: boolean;
  } | null>(null);

  // Compute default bottom-right of the full window, accounting for sidebar
  const getDefaultPosition = useCallback(() => {
    return {
      x: window.innerWidth - PANEL_WIDTH - EDGE_MARGIN - SIDEBAR_WIDTH,
      y: window.innerHeight - PANEL_HEIGHT - EDGE_MARGIN,
    };
  }, []);

  const handleClosePanel = useCallback(() => {
    widgetInstanceRef.current?.unmount();
    widgetInstanceRef.current = null;
    widgetMountedRef.current = false;
    closeSurveyPanel();
  }, [closeSurveyPanel]);

  // Mobile sheet: slides up on open, follows a downward swipe, slides out on close
  const reduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const [sheetShown, setSheetShown] = useState(false);
  const [sheetOffset, setSheetOffset] = useState(0);
  const [sheetDragging, setSheetDragging] = useState(false);
  const [sheetHeight, setSheetHeight] = useState(0);
  const sheetDragRef = useRef<{
    startY: number;
    lastY: number;
    lastTime: number;
    velocity: number;
  } | null>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!panelOpen || !isMobile) {
      setSheetShown(false);
      setSheetOffset(0);
      return;
    }
    // Two frames so the off-screen start position paints before the slide-up
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => setSheetShown(true));
    });
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
    };
  }, [panelOpen, isMobile]);

  useEffect(
    () => () => {
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    },
    []
  );

  const dismissPanel = useCallback(() => {
    if (!isMobile || reduceMotion) {
      handleClosePanel();
      return;
    }
    if (closeTimerRef.current) return;
    setSheetShown(false);
    closeTimerRef.current = setTimeout(() => {
      closeTimerRef.current = null;
      handleClosePanel();
    }, SHEET_ANIMATION_MS);
  }, [isMobile, reduceMotion, handleClosePanel]);

  // The widget effect reads the closer through a ref so a breakpoint change
  // doesn't remount Typeform and wipe the voter's answers.
  const dismissPanelRef = useRef(dismissPanel);
  dismissPanelRef.current = dismissPanel;

  const handleSheetPointerDown = useCallback((e: React.PointerEvent) => {
    if ((e.target as HTMLElement).closest('button')) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    sheetDragRef.current = {
      startY: e.clientY,
      lastY: e.clientY,
      lastTime: e.timeStamp,
      velocity: 0,
    };
    setSheetHeight(dialogRef.current?.offsetHeight ?? window.innerHeight);
    setSheetDragging(true);
  }, []);

  const handleSheetPointerMove = useCallback((e: React.PointerEvent) => {
    const drag = sheetDragRef.current;
    if (!drag) return;
    const dt = e.timeStamp - drag.lastTime;
    if (dt > 0) drag.velocity = (e.clientY - drag.lastY) / dt;
    drag.lastY = e.clientY;
    drag.lastTime = e.timeStamp;
    setSheetOffset(sheetDragOffset(drag.startY, e.clientY));
  }, []);

  const handleSheetPointerEnd = useCallback(
    (e: React.PointerEvent) => {
      const drag = sheetDragRef.current;
      if (!drag) return;
      sheetDragRef.current = null;
      setSheetDragging(false);
      // A cancelled gesture (OS/browser interruption) isn't a release; spring back.
      if (e.type === 'pointercancel') {
        setSheetOffset(0);
        return;
      }
      const dy = sheetDragOffset(drag.startY, e.clientY);
      const height = dialogRef.current?.offsetHeight ?? window.innerHeight;
      const velocity = releaseVelocity(drag.velocity, e.timeStamp - drag.lastTime);
      if (shouldDismissSheet(dy, velocity, height)) {
        dismissPanel();
      } else {
        setSheetOffset(0);
      }
    },
    [dismissPanel]
  );

  const handleDialogKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key !== 'Tab' || !dialogRef.current) return;
    const focusable = dialogRef.current.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (!first || !last) return;
    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }, []);

  // Mount the Typeform widget when the panel opens (both mobile and desktop)
  useEffect(() => {
    if (!panelOpen || !metadataReady || !widgetContainerRef.current || widgetMountedRef.current)
      return;

    widgetMountedRef.current = true;
    widgetInstanceRef.current = createWidget(TYPEFORM_FORM_ID, {
      container: widgetContainerRef.current,
      // Typeform otherwise swaps to its own fullscreen mode on small screens,
      // stacking a second close button and header inside our panel.
      inlineOnMobile: true,
      hidden: {
        ...buildFeedbackFields({
          session_id: sessionId,
          survey_version: SURVEY_VERSION,
          participant_id: participantId,
          campaign_code: campaignCode,
        }),
        ...metadataRef.current,
      },
      onSubmit: () => {
        markSurveyCompleted();
        setTimeout(() => dismissPanelRef.current(), 3000);
      },
    });
    return () => {
      widgetInstanceRef.current?.unmount();
      widgetInstanceRef.current = null;
      widgetMountedRef.current = false;
    };
  }, [
    panelOpen,
    metadataReady,
    sessionId,
    participantId,
    campaignCode,
    markSurveyCompleted,
    handleClosePanel,
  ]);

  // Move focus into the dialog when it opens
  useEffect(() => {
    if (panelOpen && dialogRef.current) {
      const focusable = dialogRef.current.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (focusable[0]) focusable[0].focus();
    }
  }, [panelOpen]);

  // Reset position when panel opens (desktop only; mobile is a fixed bottom sheet)
  useEffect(() => {
    if (panelOpen && !hasBeenDragged && !isMobile) {
      setPosition(getDefaultPosition());
    }
  }, [panelOpen, hasBeenDragged, getDefaultPosition, isMobile]);

  // Drag handlers
  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      const target = e.target as HTMLElement;
      if (target.closest('button')) return;

      e.preventDefault();
      (e.target as HTMLElement).setPointerCapture(e.pointerId);
      dragRef.current = {
        startX: e.clientX,
        startY: e.clientY,
        startPosX: position.x,
        startPosY: position.y,
        dragging: true,
        moved: false,
      };
    },
    [position]
  );

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    if (!dragRef.current?.dragging) return;
    e.preventDefault();

    const dx = e.clientX - dragRef.current.startX;
    const dy = e.clientY - dragRef.current.startY;

    if (Math.abs(dx) > 3 || Math.abs(dy) > 3) {
      dragRef.current.moved = true;
    }

    const newX = Math.max(
      EDGE_MARGIN,
      Math.min(window.innerWidth - PANEL_WIDTH - EDGE_MARGIN, dragRef.current.startPosX + dx)
    );
    const newY = Math.max(
      EDGE_MARGIN,
      Math.min(window.innerHeight - PANEL_HEIGHT - EDGE_MARGIN, dragRef.current.startPosY + dy)
    );

    setPosition({ x: newX, y: newY });
  }, []);

  const handlePointerUp = useCallback(() => {
    if (dragRef.current?.moved) {
      setHasBeenDragged(true);
    }
    dragRef.current = null;
  }, []);

  return (
    <>
      {/* Mobile: bottom sheet over a dimmed backdrop, matching the mobile app's survey sheet */}
      {panelOpen && isMobile && (
        <Box
          onClick={dismissPanel}
          aria-hidden="true"
          sx={{
            position: 'fixed',
            inset: 0,
            zIndex: 1899,
            backgroundColor: '#000',
            opacity: sheetShown ? 0.5 * (1 - (sheetHeight ? sheetOffset / sheetHeight : 0)) : 0,
            transition:
              sheetDragging || reduceMotion ? 'none' : `opacity ${SHEET_ANIMATION_MS}ms ease`,
          }}
        />
      )}
      {/* Desktop: floating draggable panel — opened from explicit survey buttons */}
      {panelOpen && (
        <Box
          ref={dialogRef}
          role="dialog"
          aria-label={t('feedbackSurvey.dialogAriaLabel')}
          aria-modal="true"
          onKeyDown={handleDialogKeyDown}
          sx={{
            position: 'fixed',
            zIndex: 1900,
            overflow: 'hidden',
            ...(isMobile
              ? {
                  left: 0,
                  right: 0,
                  bottom: 0,
                  height: `calc(100dvh - ${MOBILE_SHEET_TOP_OFFSET}px)`,
                  borderTopLeftRadius: 16,
                  borderTopRightRadius: 16,
                  paddingBottom: 'env(safe-area-inset-bottom)',
                  transform:
                    sheetShown || reduceMotion
                      ? `translateY(${sheetOffset}px)`
                      : 'translateY(100%)',
                  transition:
                    sheetDragging || reduceMotion
                      ? 'none'
                      : `transform ${SHEET_ANIMATION_MS}ms cubic-bezier(0.32, 0.72, 0, 1)`,
                }
              : {
                  left: position.x,
                  top: position.y,
                  width: PANEL_WIDTH,
                  height: PANEL_HEIGHT,
                  borderRadius: BORDER_RADIUS,
                }),
            boxShadow: '0 8px 30px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.08)',
            border: '1px solid #e2e8f0',
            backgroundColor: '#ffffff',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Header drags the panel on desktop and swipes the sheet down on mobile. Only the
              header takes the gesture, so scrolling inside the survey never dismisses it. */}
          <Box
            {...(isMobile
              ? {
                  onPointerDown: handleSheetPointerDown,
                  onPointerMove: handleSheetPointerMove,
                  onPointerUp: handleSheetPointerEnd,
                  onPointerCancel: handleSheetPointerEnd,
                }
              : {
                  onPointerDown: handlePointerDown,
                  onPointerMove: handlePointerMove,
                  onPointerUp: handlePointerUp,
                })}
            sx={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: '16px',
              py: '10px',
              borderBottom: '1px solid #f1f5f9',
              backgroundColor: '#f8fafc',
              flexShrink: 0,
              userSelect: 'none',
              ...(isMobile
                ? { pt: '22px', touchAction: 'none' }
                : { cursor: 'grab', '&:active': { cursor: 'grabbing' } }),
            }}
          >
            {isMobile && (
              <Box
                aria-hidden="true"
                sx={{
                  position: 'absolute',
                  top: '8px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: '36px',
                  height: '4px',
                  borderRadius: '2px',
                  backgroundColor: '#cbd5e1',
                }}
              />
            )}
            <Typography
              sx={{
                fontSize: '13px',
                fontWeight: 600,
                color: '#334155',
                fontFamily: "'DM Sans', sans-serif",
              }}
            >
              {t('feedbackSurvey.title')}
            </Typography>
            <IconButton
              onClick={dismissPanel}
              aria-label={t('feedbackSurvey.closeAriaLabel')}
              size="small"
              sx={{
                width: { xs: '44px', md: '24px' },
                height: { xs: '44px', md: '24px' },
                mr: { xs: '-10px', md: 0 },
                my: { xs: '-10px', md: 0 },
                color: '#94a3b8',
                '&:hover': { color: '#475569', backgroundColor: '#f1f5f9' },
              }}
            >
              <XMarkIcon width={16} height={16} />
            </IconButton>
          </Box>

          {/* Typeform widget container — iframe fills the space below the header */}
          <Box
            ref={widgetContainerRef}
            sx={{
              flex: 1,
              minHeight: 0,
              overflow: 'hidden',
              position: 'relative',
              '& iframe': {
                position: 'absolute !important',
                inset: '0 !important',
                width: '100% !important',
                height: '100% !important',
                border: 'none !important',
              },
              '& > div': {
                position: 'absolute !important',
                top: '0 !important',
                left: '0 !important',
                width: '100% !important',
                height: '100% !important',
              },
            }}
          />
        </Box>
      )}
    </>
  );
};

export default FeedbackSurveyChip;
